import React, { useState, useEffect } from 'react';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
    Settings,
    Palette,
    Truck,
    Building2,
    Tags,
    Users,
    Plus,
    Pencil,
    Trash2,
    Save,
    Upload,
    Loader2,
    Shield,
    AlertTriangle,
    Bell,
    Send,
    Mail,
    MessageCircle,
    Download,
} from 'lucide-react';
import { ImportarExcel } from '../../components/diesel/ImportarExcel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DieselConfiguracionPage() {
    const { config, refreshConfig, hasPermission } = useDiesel();
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Estado para configuración general
    const [configForm, setConfigForm] = useState({
        nombre_sistema: '',
        umbral_alerta_global: 10,
        color_primario: '#D4AF37',
        color_secundario: '#1a1a1a',
        color_acento: '#ffffff',
        emails_alerta: '',
        brevo_api_key: '',
        telegram_bot_token: '',
        telegram_chat_id: '',
        telegram_activo: false,
        email_activo: false,
        push_activo: false,
    });

    // Estado para catálogos
    const [tipos, setTipos] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [modelos, setModelos] = useState([]);
    const [roles, setRoles] = useState([]);
    const [usuariosRoles, setUsuariosRoles] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [permisosDisponibles, setPermisosDisponibles] = useState({});
    const [tiposLicencia, setTiposLicencia] = useState([]);
    const [dialogLicencia, setDialogLicencia] = useState({ open: false, data: null });

    // Dialogs
    const [dialogTipo, setDialogTipo] = useState({ open: false, data: null });
    const [dialogMarca, setDialogMarca] = useState({ open: false, data: null });
    const [dialogModelo, setDialogModelo] = useState({ open: false, data: null });
    const [dialogRol, setDialogRol] = useState({ open: false, data: null });
    const [dialogAsignar, setDialogAsignar] = useState({ open: false, data: null });

    const token = localStorage.getItem('token');
    const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };

    useEffect(() => {
        if (config) {
            setConfigForm({
                nombre_sistema: config.nombre_sistema || '',
                umbral_alerta_global: config.umbral_alerta_global || 10,
                color_primario: config.color_primario || '#D4AF37',
                color_secundario: config.color_secundario || '#1a1a1a',
                color_acento: config.color_acento || '#ffffff',
                emails_alerta: config.emails_alerta?.join(', ') || '',
                brevo_api_key: config.brevo_api_key || '',
                telegram_bot_token: config.telegram_bot_token || '',
                telegram_chat_id: config.telegram_chat_id || '',
                telegram_activo: config.telegram_activo || false,
                email_activo: config.email_activo || false,
                push_activo: config.push_activo || false,
            });
        }
    }, [config]);

    useEffect(() => {
        loadCatalogos();
    }, []);

    const loadCatalogos = async () => {
        setLoading(true);
        try {
            const [tiposRes, marcasRes, modelosRes, rolesRes, usuariosRolesRes, permisosRes] = await Promise.all([
                fetch(`${API_URL}/api/diesel/tipos-vehiculo`, { headers }),
                fetch(`${API_URL}/api/diesel/marcas`, { headers }),
                fetch(`${API_URL}/api/diesel/modelos`, { headers }),
                fetch(`${API_URL}/api/diesel/roles`, { headers }),
                fetch(`${API_URL}/api/diesel/usuarios-roles`, { headers }),
                fetch(`${API_URL}/api/diesel/permisos-disponibles`, { headers }),
            ]);

            if (tiposRes.ok) setTipos(await tiposRes.json());
            if (marcasRes.ok) setMarcas(await marcasRes.json());
            if (modelosRes.ok) setModelos(await modelosRes.json());
            if (rolesRes.ok) setRoles(await rolesRes.json());
            if (usuariosRolesRes.ok) setUsuariosRoles(await usuariosRolesRes.json());
            if (permisosRes.ok) setPermisosDisponibles(await permisosRes.json());

            // Cargar tipos de licencia
            const licRes = await fetch(`${API_URL}/api/diesel/tipos-licencia`, { headers });
            if (licRes.ok) setTiposLicencia(await licRes.json());

            // Cargar usuarios para asignación
            const usuariosRes = await fetch(`${API_URL}/api/usuarios`, { headers });
            if (usuariosRes.ok) setUsuarios(await usuariosRes.json());
        } catch (err) {
            console.error('Error loading catalogos:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConfig = async () => {
        setSaving(true);
        try {
            const data = {
                ...configForm,
                emails_alerta: configForm.emails_alerta.split(',').map(e => e.trim()).filter(Boolean),
            };

            const res = await fetch(`${API_URL}/api/diesel/config`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data),
            });

            if (res.ok) {
                toast.success('Configuración guardada');
                refreshConfig();
            } else {
                throw new Error('Error al guardar');
            }
        } catch (err) {
            toast.error('Error al guardar configuración');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch(`${API_URL}/api/diesel/config/logo`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });

            if (res.ok) {
                toast.success('Logo actualizado');
                refreshConfig();
            }
        } catch (err) {
            toast.error('Error al subir logo');
        }
    };

    const handleInitRoles = async () => {
        try {
            const res = await fetch(`${API_URL}/api/diesel/init-roles`, {
                method: 'POST',
                headers,
            });

            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
                loadCatalogos();
            }
        } catch (err) {
            toast.error('Error al inicializar roles');
        }
    };

    // CRUD Tipos
    const handleSaveTipo = async () => {
        const data = dialogTipo.data;
        const method = data.id ? 'PUT' : 'POST';
        const url = data.id 
            ? `${API_URL}/api/diesel/tipos-vehiculo/${data.id}`
            : `${API_URL}/api/diesel/tipos-vehiculo`;

        try {
            const res = await fetch(url, { method, headers, body: JSON.stringify(data) });
            if (res.ok) {
                toast.success(data.id ? 'Tipo actualizado' : 'Tipo creado');
                setDialogTipo({ open: false, data: null });
                loadCatalogos();
            }
        } catch (err) {
            toast.error('Error al guardar tipo');
        }
    };

    const handleDeleteTipo = async (id) => {
        if (!confirm('¿Eliminar este tipo de vehículo?')) return;
        try {
            const res = await fetch(`${API_URL}/api/diesel/tipos-vehiculo/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (res.ok) {
                toast.success('Tipo eliminado');
                loadCatalogos();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al eliminar');
            }
        } catch (err) {
            toast.error('Error al eliminar tipo');
        }
    };

    // CRUD Tipos de Licencia
    const handleSaveLicencia = async () => {
        const data = dialogLicencia.data;
        if (!data.codigo || !data.nombre) { toast.error('Codigo y nombre requeridos'); return; }
        const method = data.id ? 'PUT' : 'POST';
        const url = data.id
            ? `${API_URL}/api/diesel/tipos-licencia/${data.id}`
            : `${API_URL}/api/diesel/tipos-licencia`;
        try {
            const res = await fetch(url, { method, headers, body: JSON.stringify(data) });
            if (res.ok) {
                toast.success(data.id ? 'Tipo actualizado' : 'Tipo creado');
                setDialogLicencia({ open: false, data: null });
                loadCatalogos();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error al guardar'); }
    };

    const handleDeleteLicencia = async (id) => {
        if (!confirm('¿Eliminar este tipo de licencia?')) return;
        try {
            const res = await fetch(`${API_URL}/api/diesel/tipos-licencia/${id}`, { method: 'DELETE', headers });
            if (res.ok) { toast.success('Tipo eliminado'); loadCatalogos(); }
            else { const err = await res.json(); toast.error(err.detail || 'Error'); }
        } catch { toast.error('Error al eliminar'); }
    };

    const handleInitLicencias = async () => {
        try {
            const res = await fetch(`${API_URL}/api/diesel/tipos-licencia/init`, { method: 'POST', headers });
            if (res.ok) { const d = await res.json(); toast.success(d.message); loadCatalogos(); }
        } catch { toast.error('Error'); }
    };

    // CRUD Marcas
    const handleSaveMarca = async () => {
        const data = dialogMarca.data;
        const method = data.id ? 'PUT' : 'POST';
        const url = data.id 
            ? `${API_URL}/api/diesel/marcas/${data.id}`
            : `${API_URL}/api/diesel/marcas`;

        try {
            const res = await fetch(url, { method, headers, body: JSON.stringify(data) });
            if (res.ok) {
                toast.success(data.id ? 'Marca actualizada' : 'Marca creada');
                setDialogMarca({ open: false, data: null });
                loadCatalogos();
            }
        } catch (err) {
            toast.error('Error al guardar marca');
        }
    };

    const handleDeleteMarca = async (id) => {
        if (!confirm('¿Eliminar esta marca?')) return;
        try {
            const res = await fetch(`${API_URL}/api/diesel/marcas/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (res.ok) {
                toast.success('Marca eliminada');
                loadCatalogos();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al eliminar');
            }
        } catch (err) {
            toast.error('Error al eliminar marca');
        }
    };

    // CRUD Modelos
    const handleSaveModelo = async () => {
        const data = dialogModelo.data;
        const method = data.id ? 'PUT' : 'POST';
        const url = data.id 
            ? `${API_URL}/api/diesel/modelos/${data.id}`
            : `${API_URL}/api/diesel/modelos`;

        try {
            const res = await fetch(url, { method, headers, body: JSON.stringify(data) });
            if (res.ok) {
                toast.success(data.id ? 'Modelo actualizado' : 'Modelo creado');
                setDialogModelo({ open: false, data: null });
                loadCatalogos();
            }
        } catch (err) {
            toast.error('Error al guardar modelo');
        }
    };

    const handleDeleteModelo = async (id) => {
        if (!confirm('¿Eliminar este modelo?')) return;
        try {
            const res = await fetch(`${API_URL}/api/diesel/modelos/${id}`, {
                method: 'DELETE',
                headers,
            });
            if (res.ok) {
                toast.success('Modelo eliminado');
                loadCatalogos();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al eliminar');
            }
        } catch (err) {
            toast.error('Error al eliminar modelo');
        }
    };

    // CRUD Roles
    const handleSaveRol = async () => {
        const data = dialogRol.data;
        const method = data.id ? 'PUT' : 'POST';
        const url = data.id 
            ? `${API_URL}/api/diesel/roles/${data.id}`
            : `${API_URL}/api/diesel/roles`;

        try {
            const res = await fetch(url, { method, headers, body: JSON.stringify(data) });
            if (res.ok) {
                toast.success(data.id ? 'Rol actualizado' : 'Rol creado');
                setDialogRol({ open: false, data: null });
                loadCatalogos();
            }
        } catch (err) {
            toast.error('Error al guardar rol');
        }
    };

    // Asignar rol a usuario
    const handleAsignarRol = async () => {
        const data = dialogAsignar.data;
        try {
            const res = await fetch(`${API_URL}/api/diesel/usuarios-roles`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
            });
            if (res.ok) {
                toast.success('Rol asignado');
                setDialogAsignar({ open: false, data: null });
                loadCatalogos();
            }
        } catch (err) {
            toast.error('Error al asignar rol');
        }
    };

    const handleQuitarRol = async (usuarioId) => {
        if (!confirm('¿Quitar rol de este usuario?')) return;
        try {
            const res = await fetch(`${API_URL}/api/diesel/usuarios-roles/${usuarioId}`, {
                method: 'DELETE',
                headers,
            });
            if (res.ok) {
                toast.success('Rol removido');
                loadCatalogos();
            }
        } catch (err) {
            toast.error('Error al quitar rol');
        }
    };

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                <div>
                    <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                        Configuración
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Administra el sistema de control de combustible
                    </p>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-3 md:grid-cols-6 w-full">
                        <TabsTrigger value="general" className="gap-2">
                            <Settings className="h-4 w-4" />
                            <span className="hidden md:inline">General</span>
                        </TabsTrigger>
                        <TabsTrigger value="notificaciones" className="gap-2">
                            <Bell className="h-4 w-4" />
                            <span className="hidden md:inline">Notificaciones</span>
                        </TabsTrigger>
                        <TabsTrigger value="tipos" className="gap-2">
                            <Tags className="h-4 w-4" />
                            <span className="hidden md:inline">Tipos</span>
                        </TabsTrigger>
                        <TabsTrigger value="marcas" className="gap-2">
                            <Building2 className="h-4 w-4" />
                            <span className="hidden md:inline">Marcas</span>
                        </TabsTrigger>
                        <TabsTrigger value="modelos" className="gap-2">
                            <Truck className="h-4 w-4" />
                            <span className="hidden md:inline">Modelos</span>
                        </TabsTrigger>
                        <TabsTrigger value="usuarios" className="gap-2">
                            <Users className="h-4 w-4" />
                            <span className="hidden md:inline">Usuarios</span>
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab General */}
                    <TabsContent value="general" className="space-y-4">
                        <Card className="border-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Palette className="h-5 w-5" />
                                    Personalización
                                </CardTitle>
                                <CardDescription>
                                    Configura la apariencia y nombre del sistema
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Nombre del Sistema</Label>
                                        <Input
                                            value={configForm.nombre_sistema}
                                            onChange={(e) => setConfigForm({...configForm, nombre_sistema: e.target.value})}
                                            placeholder="Ej: AMCOM Diesel"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Logo</Label>
                                        <div className="flex items-center gap-2">
                                            {config?.logo_url && (
                                                <img src={config.logo_url} alt="Logo" className="h-10 rounded" />
                                            )}
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoUpload}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Color Primario</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="color"
                                                value={configForm.color_primario}
                                                onChange={(e) => setConfigForm({...configForm, color_primario: e.target.value})}
                                                className="w-14 h-10 p-1"
                                            />
                                            <Input
                                                value={configForm.color_primario}
                                                onChange={(e) => setConfigForm({...configForm, color_primario: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Color Secundario</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="color"
                                                value={configForm.color_secundario}
                                                onChange={(e) => setConfigForm({...configForm, color_secundario: e.target.value})}
                                                className="w-14 h-10 p-1"
                                            />
                                            <Input
                                                value={configForm.color_secundario}
                                                onChange={(e) => setConfigForm({...configForm, color_secundario: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Color Acento</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="color"
                                                value={configForm.color_acento}
                                                onChange={(e) => setConfigForm({...configForm, color_acento: e.target.value})}
                                                className="w-14 h-10 p-1"
                                            />
                                            <Input
                                                value={configForm.color_acento}
                                                onChange={(e) => setConfigForm({...configForm, color_acento: e.target.value})}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5" />
                                    Alertas
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Umbral de Alerta Global (%)</Label>
                                    <Input
                                        type="number"
                                        value={configForm.umbral_alerta_global}
                                        onChange={(e) => setConfigForm({...configForm, umbral_alerta_global: parseFloat(e.target.value)})}
                                        min="1"
                                        max="100"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Se genera alerta si el rendimiento varia mas de este porcentaje.
                                        Configure los canales de notificacion en la pestana "Notificaciones".
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="flex justify-end">
                            <Button onClick={handleSaveConfig} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Guardar Configuración
                            </Button>
                        </div>
                    </TabsContent>

                    {/* Tab Notificaciones */}
                    <TabsContent value="notificaciones" className="space-y-4">
                        {/* Telegram */}
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <MessageCircle className="h-5 w-5 text-[#0088cc]" />
                                            Telegram Bot
                                        </CardTitle>
                                        <CardDescription>Envia alertas a un grupo de Telegram (gratis)</CardDescription>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className="text-sm text-muted-foreground">Activo</span>
                                        <Checkbox
                                            checked={configForm.telegram_activo}
                                            onCheckedChange={(v) => setConfigForm({...configForm, telegram_activo: !!v})}
                                            data-testid="toggle-telegram"
                                        />
                                    </label>
                                </div>
                            </CardHeader>
                            {configForm.telegram_activo && (
                                <CardContent className="space-y-4">
                                    <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground space-y-1">
                                        <p><strong>Como configurar:</strong></p>
                                        <p>1. Abra Telegram y busque @BotFather</p>
                                        <p>2. Envie /newbot y siga las instrucciones</p>
                                        <p>3. Copie el Token del bot</p>
                                        <p>4. Agregue el bot a un grupo de supervisores</p>
                                        <p>5. Para obtener el Chat ID, agregue @RawDataBot al grupo y copie el chat id</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Token del Bot</Label>
                                            <Input
                                                value={configForm.telegram_bot_token}
                                                onChange={(e) => setConfigForm({...configForm, telegram_bot_token: e.target.value})}
                                                placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v..."
                                                type="password"
                                                data-testid="input-telegram-token"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Chat ID del Grupo</Label>
                                            <Input
                                                value={configForm.telegram_chat_id}
                                                onChange={(e) => setConfigForm({...configForm, telegram_chat_id: e.target.value})}
                                                placeholder="-1001234567890"
                                                data-testid="input-telegram-chatid"
                                            />
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                            try {
                                                const res = await fetch(`${API_URL}/api/diesel/notificaciones/test-telegram`, {
                                                    method: 'POST', headers,
                                                    body: JSON.stringify({
                                                        telegram_bot_token: configForm.telegram_bot_token,
                                                        telegram_chat_id: configForm.telegram_chat_id,
                                                    }),
                                                });
                                                const data = await res.json();
                                                if (data.ok) {
                                                    toast.success(`Telegram conectado${data.bot_name ? ` (${data.bot_name})` : ''}`);
                                                } else {
                                                    toast.error(data.message || 'Error de conexion');
                                                }
                                            } catch { toast.error('Error al probar'); }
                                        }}
                                        disabled={!configForm.telegram_bot_token || !configForm.telegram_chat_id}
                                        data-testid="btn-test-telegram"
                                    >
                                        <Send className="h-4 w-4 mr-2" />
                                        Probar Conexion
                                    </Button>
                                </CardContent>
                            )}
                        </Card>

                        {/* Email */}
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Mail className="h-5 w-5 text-emerald-600" />
                                            Email (Brevo)
                                        </CardTitle>
                                        <CardDescription>Envia alertas por email a supervisores</CardDescription>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className="text-sm text-muted-foreground">Activo</span>
                                        <Checkbox
                                            checked={configForm.email_activo}
                                            onCheckedChange={(v) => setConfigForm({...configForm, email_activo: !!v})}
                                            data-testid="toggle-email"
                                        />
                                    </label>
                                </div>
                            </CardHeader>
                            {configForm.email_activo && (
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>API Key de Brevo</Label>
                                            <Input
                                                value={configForm.brevo_api_key}
                                                onChange={(e) => setConfigForm({...configForm, brevo_api_key: e.target.value})}
                                                placeholder="xkeysib-..."
                                                type="password"
                                                data-testid="input-brevo-key"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Emails Destinatarios (separados por coma)</Label>
                                            <Input
                                                value={configForm.emails_alerta}
                                                onChange={(e) => setConfigForm({...configForm, emails_alerta: e.target.value})}
                                                placeholder="admin@empresa.cl, supervisor@empresa.cl"
                                                data-testid="input-emails-alerta"
                                            />
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={async () => {
                                            const firstEmail = configForm.emails_alerta.split(',')[0]?.trim();
                                            if (!firstEmail) { toast.error('Ingrese al menos un email'); return; }
                                            try {
                                                const res = await fetch(`${API_URL}/api/diesel/notificaciones/test-email`, {
                                                    method: 'POST', headers,
                                                    body: JSON.stringify({ brevo_api_key: configForm.brevo_api_key, email: firstEmail }),
                                                });
                                                const data = await res.json();
                                                if (data.ok) toast.success('Email de prueba enviado');
                                                else toast.error(data.message || 'Error al enviar');
                                            } catch { toast.error('Error al probar'); }
                                        }}
                                        disabled={!configForm.brevo_api_key || !configForm.emails_alerta}
                                        data-testid="btn-test-email"
                                    >
                                        <Send className="h-4 w-4 mr-2" />
                                        Enviar Email de Prueba
                                    </Button>
                                </CardContent>
                            )}
                        </Card>

                        {/* Push */}
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Bell className="h-5 w-5 text-amber-600" />
                                            Push (Navegador)
                                        </CardTitle>
                                        <CardDescription>Notificaciones en el navegador sin configuracion adicional</CardDescription>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className="text-sm text-muted-foreground">Activo</span>
                                        <Checkbox
                                            checked={configForm.push_activo}
                                            onCheckedChange={(v) => setConfigForm({...configForm, push_activo: !!v})}
                                            data-testid="toggle-push"
                                        />
                                    </label>
                                </div>
                            </CardHeader>
                            {configForm.push_activo && (
                                <CardContent>
                                    <p className="text-sm text-muted-foreground">
                                        Las alertas se mostraran como notificaciones internas del sistema.
                                        No requiere configuracion adicional.
                                    </p>
                                </CardContent>
                            )}
                        </Card>

                        {/* Manual PDF */}
                        <Card className="border-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Download className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                    Manual del Sistema
                                </CardTitle>
                                <CardDescription>Descargue el manual completo del modulo Diesel en formato PDF</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Button
                                    variant="outline"
                                    onClick={async () => {
                                        try {
                                            const res = await fetch(`${API_URL}/api/diesel/manual-pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
                                            if (res.ok) {
                                                const blob = await res.blob();
                                                const link = document.createElement('a');
                                                link.href = URL.createObjectURL(blob);
                                                link.download = 'Manual_Diesel.pdf';
                                                link.click();
                                                URL.revokeObjectURL(link.href);
                                                toast.success('Manual descargado');
                                            } else toast.error('Error al generar manual');
                                        } catch { toast.error('Error de conexion'); }
                                    }}
                                    data-testid="btn-download-manual"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Descargar Manual PDF
                                </Button>
                            </CardContent>
                        </Card>

                        <div className="flex justify-end">
                            <Button onClick={handleSaveConfig} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Guardar Configuracion
                            </Button>
                        </div>
                    </TabsContent>

                    {/* Tab Tipos */}
                    <TabsContent value="tipos">
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Tipos de Vehiculo</CardTitle>
                                    <div className="flex gap-2">
                                        <ImportarExcel tipo="tipos-vehiculo" label="Tipos de Vehiculo" onSuccess={loadCatalogos} />
                                        <Button onClick={() => setDialogTipo({ open: true, data: { nombre: '', umbral_alerta: null } })}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Nuevo Tipo
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nombre</TableHead>
                                            <TableHead>Descripción</TableHead>
                                            <TableHead>Umbral Alerta</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {tipos.map((tipo) => (
                                            <TableRow key={tipo.id}>
                                                <TableCell className="font-medium">{tipo.nombre}</TableCell>
                                                <TableCell>{tipo.descripcion || '-'}</TableCell>
                                                <TableCell>{tipo.umbral_alerta ? `${tipo.umbral_alerta}%` : 'Global'}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => setDialogTipo({ open: true, data: tipo })}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => handleDeleteTipo(tipo.id)}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {tipos.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                                                    No hay tipos registrados
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>

                        {/* Tipos de Licencia */}
                        <Card className="border-2 mt-4">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="flex items-center gap-2">
                                        <Shield className="h-5 w-5" />
                                        Tipos de Licencia
                                    </CardTitle>
                                    <div className="flex gap-2">
                                        {tiposLicencia.length === 0 && (
                                            <Button variant="outline" size="sm" onClick={handleInitLicencias} data-testid="btn-init-licencias">
                                                Cargar Predefinidos
                                            </Button>
                                        )}
                                        <Button onClick={() => setDialogLicencia({ open: true, data: { codigo: '', nombre: '', descripcion: '' } })} data-testid="btn-nueva-licencia">
                                            <Plus className="h-4 w-4 mr-2" />
                                            Nuevo Tipo
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Codigo</TableHead>
                                            <TableHead>Nombre</TableHead>
                                            <TableHead>Descripcion</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {tiposLicencia.map((tl) => (
                                            <TableRow key={tl.id}>
                                                <TableCell className="font-mono font-bold">{tl.codigo}</TableCell>
                                                <TableCell>{tl.nombre}</TableCell>
                                                <TableCell className="text-muted-foreground">{tl.descripcion || '-'}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => setDialogLicencia({ open: true, data: tl })}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => handleDeleteLicencia(tl.id)}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {tiposLicencia.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                                                    No hay tipos de licencia. Use "Cargar Predefinidos" para iniciar.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab Marcas */}
                    <TabsContent value="marcas">
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Marcas</CardTitle>
                                    <div className="flex gap-2">
                                        <ImportarExcel tipo="marcas" label="Marcas" onSuccess={loadCatalogos} />
                                        <Button onClick={() => setDialogMarca({ open: true, data: { nombre: '', umbral_alerta: null } })}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Nueva Marca
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Nombre</TableHead>
                                            <TableHead>Umbral Alerta</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {marcas.map((marca) => (
                                            <TableRow key={marca.id}>
                                                <TableCell className="font-medium">{marca.nombre}</TableCell>
                                                <TableCell>{marca.umbral_alerta ? `${marca.umbral_alerta}%` : 'Global'}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => setDialogMarca({ open: true, data: marca })}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => handleDeleteMarca(marca.id)}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {marcas.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                                                    No hay marcas registradas
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab Modelos */}
                    <TabsContent value="modelos">
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle>Modelos de Vehiculo</CardTitle>
                                    <div className="flex gap-2">
                                        <ImportarExcel tipo="modelos" label="Modelos" onSuccess={loadCatalogos} />
                                        <Button 
                                            onClick={() => setDialogModelo({ open: true, data: { nombre: '', marca_id: '', rendimiento_fabricante: 0 } })}
                                            disabled={marcas.length === 0}
                                        >
                                            <Plus className="h-4 w-4 mr-2" />
                                            Nuevo Modelo
                                        </Button>
                                    </div>
                                </div>
                                {marcas.length === 0 && (
                                    <CardDescription className="text-amber-600">
                                        Primero debe registrar al menos una marca
                                    </CardDescription>
                                )}
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Marca</TableHead>
                                            <TableHead>Modelo</TableHead>
                                            <TableHead>Tipo</TableHead>
                                            <TableHead>Rendimiento (km/l)</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {modelos.map((modelo) => (
                                            <TableRow key={modelo.id}>
                                                <TableCell>{modelo.marca_nombre}</TableCell>
                                                <TableCell className="font-medium">{modelo.nombre}</TableCell>
                                                <TableCell>{modelo.tipo_vehiculo_nombre || '-'}</TableCell>
                                                <TableCell>{modelo.rendimiento_fabricante}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => setDialogModelo({ open: true, data: modelo })}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => handleDeleteModelo(modelo.id)}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {modelos.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                                    No hay modelos registrados
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab Usuarios */}
                    <TabsContent value="usuarios" className="space-y-4">
                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Shield className="h-5 w-5" />
                                            Roles
                                        </CardTitle>
                                        <CardDescription>Define los roles y permisos del sistema</CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" onClick={handleInitRoles}>
                                            Inicializar Roles
                                        </Button>
                                        <Button onClick={() => setDialogRol({ open: true, data: { nombre: '', descripcion: '', permisos: [] } })}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Nuevo Rol
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Rol</TableHead>
                                            <TableHead>Descripción</TableHead>
                                            <TableHead>Permisos</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {roles.map((rol) => (
                                            <TableRow key={rol.id}>
                                                <TableCell className="font-medium">
                                                    {rol.nombre}
                                                    {rol.es_predefinido && (
                                                        <span className="ml-2 text-xs bg-muted px-2 py-0.5 rounded">Predefinido</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>{rol.descripcion || '-'}</TableCell>
                                                <TableCell>{rol.permisos?.length || 0} permisos</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => setDialogRol({ open: true, data: rol })}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>

                        <Card className="border-2">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle>Usuarios con Acceso</CardTitle>
                                        <CardDescription>Asigna roles a los usuarios</CardDescription>
                                    </div>
                                    <Button onClick={() => setDialogAsignar({ open: true, data: { usuario_id: '', rol_id: '' } })}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Asignar Rol
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Usuario</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Rol Asignado</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {usuariosRoles.map((asig) => (
                                            <TableRow key={asig.id}>
                                                <TableCell className="font-medium">{asig.usuario?.nombre}</TableCell>
                                                <TableCell>{asig.usuario?.email}</TableCell>
                                                <TableCell>{asig.rol?.nombre}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => handleQuitarRol(asig.usuario_id)}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {usuariosRoles.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                                                    No hay usuarios con rol asignado
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Dialogs */}
            {/* Dialog Tipo Licencia */}
            <Dialog open={dialogLicencia.open} onOpenChange={(open) => !open && setDialogLicencia({ open: false, data: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{dialogLicencia.data?.id ? 'Editar' : 'Nuevo'} Tipo de Licencia</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Codigo</Label>
                                <Input
                                    value={dialogLicencia.data?.codigo || ''}
                                    onChange={(e) => setDialogLicencia({ ...dialogLicencia, data: { ...dialogLicencia.data, codigo: e.target.value } })}
                                    placeholder="Ej: B, C, D"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nombre</Label>
                                <Input
                                    value={dialogLicencia.data?.nombre || ''}
                                    onChange={(e) => setDialogLicencia({ ...dialogLicencia, data: { ...dialogLicencia.data, nombre: e.target.value } })}
                                    placeholder="Ej: Clase B"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Descripcion</Label>
                            <Input
                                value={dialogLicencia.data?.descripcion || ''}
                                onChange={(e) => setDialogLicencia({ ...dialogLicencia, data: { ...dialogLicencia.data, descripcion: e.target.value } })}
                                placeholder="Ej: Automoviles y camionetas hasta 3.500 kg"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogLicencia({ open: false, data: null })}>Cancelar</Button>
                        <Button onClick={handleSaveLicencia}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Tipo */}
            <Dialog open={dialogTipo.open} onOpenChange={(open) => !open && setDialogTipo({ open: false, data: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{dialogTipo.data?.id ? 'Editar' : 'Nuevo'} Tipo de Vehículo</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre *</Label>
                            <Input
                                value={dialogTipo.data?.nombre || ''}
                                onChange={(e) => setDialogTipo({ ...dialogTipo, data: { ...dialogTipo.data, nombre: e.target.value } })}
                                placeholder="Ej: Camión, Pickup, Maquinaria"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Descripción</Label>
                            <Input
                                value={dialogTipo.data?.descripcion || ''}
                                onChange={(e) => setDialogTipo({ ...dialogTipo, data: { ...dialogTipo.data, descripcion: e.target.value } })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Umbral de Alerta (%)</Label>
                            <Input
                                type="number"
                                value={dialogTipo.data?.umbral_alerta || ''}
                                onChange={(e) => setDialogTipo({ ...dialogTipo, data: { ...dialogTipo.data, umbral_alerta: e.target.value ? parseFloat(e.target.value) : null } })}
                                placeholder="Dejar vacío para usar el global"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogTipo({ open: false, data: null })}>Cancelar</Button>
                        <Button onClick={handleSaveTipo}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Marca */}
            <Dialog open={dialogMarca.open} onOpenChange={(open) => !open && setDialogMarca({ open: false, data: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{dialogMarca.data?.id ? 'Editar' : 'Nueva'} Marca</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre *</Label>
                            <Input
                                value={dialogMarca.data?.nombre || ''}
                                onChange={(e) => setDialogMarca({ ...dialogMarca, data: { ...dialogMarca.data, nombre: e.target.value } })}
                                placeholder="Ej: Volvo, Mercedes, Caterpillar"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Umbral de Alerta (%)</Label>
                            <Input
                                type="number"
                                value={dialogMarca.data?.umbral_alerta || ''}
                                onChange={(e) => setDialogMarca({ ...dialogMarca, data: { ...dialogMarca.data, umbral_alerta: e.target.value ? parseFloat(e.target.value) : null } })}
                                placeholder="Dejar vacío para usar el global"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogMarca({ open: false, data: null })}>Cancelar</Button>
                        <Button onClick={handleSaveMarca}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Modelo */}
            <Dialog open={dialogModelo.open} onOpenChange={(open) => !open && setDialogModelo({ open: false, data: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{dialogModelo.data?.id ? 'Editar' : 'Nuevo'} Modelo</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Marca *</Label>
                            <select
                                className="w-full p-2 border rounded-md bg-background"
                                value={dialogModelo.data?.marca_id || ''}
                                onChange={(e) => setDialogModelo({ ...dialogModelo, data: { ...dialogModelo.data, marca_id: e.target.value } })}
                            >
                                <option value="">Seleccionar marca</option>
                                {marcas.map((m) => (
                                    <option key={m.id} value={m.id}>{m.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>Nombre del Modelo *</Label>
                            <Input
                                value={dialogModelo.data?.nombre || ''}
                                onChange={(e) => setDialogModelo({ ...dialogModelo, data: { ...dialogModelo.data, nombre: e.target.value } })}
                                placeholder="Ej: FH 460, Actros 2045"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Tipo de Vehículo</Label>
                            <select
                                className="w-full p-2 border rounded-md bg-background"
                                value={dialogModelo.data?.tipo_vehiculo_id || ''}
                                onChange={(e) => setDialogModelo({ ...dialogModelo, data: { ...dialogModelo.data, tipo_vehiculo_id: e.target.value || null } })}
                            >
                                <option value="">Sin tipo específico</option>
                                {tipos.map((t) => (
                                    <option key={t.id} value={t.id}>{t.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>Rendimiento Fabricante (km/l) *</Label>
                            <Input
                                type="number"
                                step="0.1"
                                value={dialogModelo.data?.rendimiento_fabricante || ''}
                                onChange={(e) => setDialogModelo({ ...dialogModelo, data: { ...dialogModelo.data, rendimiento_fabricante: parseFloat(e.target.value) } })}
                                placeholder="Ej: 3.5"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogModelo({ open: false, data: null })}>Cancelar</Button>
                        <Button onClick={handleSaveModelo}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Rol */}
            <Dialog open={dialogRol.open} onOpenChange={(open) => !open && setDialogRol({ open: false, data: null })}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{dialogRol.data?.id ? 'Editar' : 'Nuevo'} Rol</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4 flex-1 overflow-auto">
                        <div className="space-y-2">
                            <Label>Nombre del Rol *</Label>
                            <Input
                                value={dialogRol.data?.nombre || ''}
                                onChange={(e) => setDialogRol({ ...dialogRol, data: { ...dialogRol.data, nombre: e.target.value } })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Descripción</Label>
                            <Input
                                value={dialogRol.data?.descripcion || ''}
                                onChange={(e) => setDialogRol({ ...dialogRol, data: { ...dialogRol.data, descripcion: e.target.value } })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Permisos</Label>
                            <ScrollArea className="h-64 border rounded-md p-4">
                                {Object.entries(permisosDisponibles).map(([categoria, { nombre, permisos }]) => (
                                    <div key={categoria} className="mb-4">
                                        <p className="font-bold text-sm mb-2">{nombre}</p>
                                        <div className="space-y-2 pl-4">
                                            {permisos.map((p) => (
                                                <div key={p.codigo} className="flex items-center gap-2">
                                                    <Checkbox
                                                        checked={dialogRol.data?.permisos?.includes(p.codigo)}
                                                        onCheckedChange={(checked) => {
                                                            const permisos = dialogRol.data?.permisos || [];
                                                            if (checked) {
                                                                setDialogRol({
                                                                    ...dialogRol,
                                                                    data: { ...dialogRol.data, permisos: [...permisos, p.codigo] }
                                                                });
                                                            } else {
                                                                setDialogRol({
                                                                    ...dialogRol,
                                                                    data: { ...dialogRol.data, permisos: permisos.filter(x => x !== p.codigo) }
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    <span className="text-sm">{p.nombre}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </ScrollArea>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogRol({ open: false, data: null })}>Cancelar</Button>
                        <Button onClick={handleSaveRol}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Asignar Rol */}
            <Dialog open={dialogAsignar.open} onOpenChange={(open) => !open && setDialogAsignar({ open: false, data: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Asignar Rol a Usuario</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Usuario *</Label>
                            <select
                                className="w-full p-2 border rounded-md bg-background"
                                value={dialogAsignar.data?.usuario_id || ''}
                                onChange={(e) => setDialogAsignar({ ...dialogAsignar, data: { ...dialogAsignar.data, usuario_id: e.target.value } })}
                            >
                                <option value="">Seleccionar usuario</option>
                                {usuarios
                                    .filter(u => !usuariosRoles.find(ur => ur.usuario_id === u.id))
                                    .map((u) => (
                                        <option key={u.id} value={u.id}>{u.nombre} ({u.email})</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label>Rol *</Label>
                            <select
                                className="w-full p-2 border rounded-md bg-background"
                                value={dialogAsignar.data?.rol_id || ''}
                                onChange={(e) => setDialogAsignar({ ...dialogAsignar, data: { ...dialogAsignar.data, rol_id: e.target.value } })}
                            >
                                <option value="">Seleccionar rol</option>
                                {roles.map((r) => (
                                    <option key={r.id} value={r.id}>{r.nombre}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogAsignar({ open: false, data: null })}>Cancelar</Button>
                        <Button onClick={handleAsignarRol}>Asignar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DieselLayout>
    );
}
